import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import SignUpImage from '../../assets/Signup.svg';
import Logo from '../../assets/Logo.svg';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import {
  setOnboardingComplete,
  clearSignupData,
  saveOnboardingData,
  signupThunk,
} from '../../redux/slices/authSlice';
import { sendEmailOtp, resendEmailOtp } from '../../api/api';
import { toast } from 'react-hot-toast';
import { useCountry } from '../../helpers/useCountry';
import FooterAuth from '../../components/layout/FooterAuth';

export default function Onboarding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { country, region } = useCountry();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [step, setStep] = useState(1);
  const [otpMode, setOtpMode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const tempSignupData = useSelector((state) => state?.auth?.tempSignupData);
  const onboardingReduxData = useSelector(
    (state) => state?.auth?.onboardingData
  );

  const [formData, setFormData] = useState({
    fullName: onboardingReduxData?.fullName || '',
    phoneNumber: onboardingReduxData?.phoneNumber || '',
    companyName: onboardingReduxData?.companyName || '',
    jobTitle: onboardingReduxData?.jobTitle || '',
    usState: onboardingReduxData?.usState || '',
    heardAbout: onboardingReduxData?.heardAbout || '',
  });

  const [errors, setErrors] = useState({});
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    dispatch(saveOnboardingData({ [field]: value }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1 && !formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
      toast.error(newErrors.fullName);
    }
    if (step === 3 && (!formData.usState || formData.usState.trim() === '')) {
      newErrors.usState = 'Please select your state';
      toast.error(newErrors.usState);
    }

    if (step === 4 && !formData.heardAbout) {
      newErrors.heardAbout = 'Please select an option';
      toast.error(newErrors.heardAbout);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((prev) => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setErrors({});
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFinish = async () => {
    if (!tempSignupData) {
      toast.error('Please start signup again.');
      navigate('/signup');
      return;
    }

    try {
      setLoading(true);
      const res = await sendEmailOtp(tempSignupData.email);
      if (res) {
        setOtpMode(true);
        setCountdown(60);
        toast.success('OTP sent to your email');
      }
    } catch (err) {
      const message =
        typeof err === 'string' ? err : err?.message || 'Failed to send OTP';

      if (message.includes('Email registered')) {
        toast.error('This email is already registered. Please log in.');
        navigate('/login');
        return;
      }

      toast.error(`${message}. Redirecting to SignUp in 5s...`);

      let redirectCountdown = 5;
      const timer = setInterval(() => {
        redirectCountdown--;
        if (redirectCountdown <= 0) {
          clearInterval(timer);
          navigate('/signup');
        }
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupVerify = async (otp) => {
    setVerifying(true);
    const payload = {
      email: tempSignupData.email,
      password: tempSignupData.password,
      otp,
      name: formData.fullName,
      phone_number: formData.phoneNumber,
      company_name: formData.companyName,
      job_title: formData.jobTitle,
      state: formData.usState,
      where_you_heard: formData.heardAbout,
      country,
      session_state: region,
    };

    const toastId = toast.loading('Verifying your account...');

    try {
      await dispatch(signupThunk(payload)).unwrap();
      toast.success('Welcome to uLegal AI!', { id: toastId });
      dispatch(clearSignupData());
      dispatch(setOnboardingComplete());
      navigate('/app');
    } catch (err) {
      toast.error(err || 'Signup verification failed', { id: toastId });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async (email) => {
    try {
      const res = await resendEmailOtp(email);
      if (res) {
        setCountdown(60);
        toast.success(res?.message || 'A new OTP has been sent!');
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to resend OTP';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    dispatch(setOnboardingComplete());
    navigate('/app');
  };

  const commonProps = {
    formData,
    setFormData,
    handleChange,
    errors,
    nextStep,
    prevStep,
    cancel: handleCancel,
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <Step1 {...commonProps} />;
      case 2:
        return <Step2 {...commonProps} />;
      case 3:
        return <Step3 {...commonProps} />;
      case 4:
        return (
          <Step4
            {...commonProps}
            handleFinish={handleFinish}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-screen w-full font-sans bg-gray-50 overflow-x-hidden">
        {/* Left Side */}
        <div className="flex flex-col md:flex-row flex-grow">
          <div className="w-full md:w-1/2 flex flex-col bg-white shadow-lg relative px-8 md:px-20 py-12">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="absolute top-6 left-4 md:left-10 right-auto w-fit"
            >
              <a href="/">
                <img
                  src={Logo}
                  alt="Legal Intelligence Logo"
                  className="w-20 h-auto"
                />
              </a>
            </motion.div>

            {/* Main Content */}

            <div className="w-full max-w-md mt-14 md:pt-16 mx-auto">
              <AnimatePresence mode="wait">
                {!otpMode ? (
                  <motion.div
                    key="onboarding-steps"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                  >
                    {/* Stepper Progress */}
                    <div className="mb-6">
                      <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden">
                        <motion.div
                          className="bg-black h-1 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${(step / 4) * 100}%` }}
                          transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Step {step}/4
                      </p>
                    </div>

                    {/* Step Content */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`step-${step}`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                      >
                        {renderStep()}
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div
                    key="otp-screen"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                  >
                    <h2 className="text-xl font-bold text-center mb-4">
                      Verify Your Email
                    </h2>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Enter the 6-digit OTP sent to{' '}
                      <span className="font-semibold">
                        {tempSignupData?.email}
                      </span>
                    </p>

                    <div className="flex justify-center gap-3 mb-4">
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <motion.input
                          key={idx}
                          type="text"
                          maxLength="1"
                          name={`otp-${idx}`}
                          className="w-12 h-12 text-center text-xl font-semibold 
         border-2 border-brand rounded-lg 
         focus:ring-2 focus:ring-brand focus:border-brand 
         text-brand placeholder-gray-400
         outline-none transition-colors duration-200"
                          whileFocus={{ scale: 1.1 }}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              ''
                            );
                            if (e.target.value && e.target.nextSibling) {
                              e.target.nextSibling.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Backspace' &&
                              !e.target.value &&
                              e.target.previousSibling
                            ) {
                              e.target.previousSibling.focus();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const paste = e.clipboardData
                              .getData('text')
                              .replace(/[^0-9]/g, '');
                            if (paste.length === 0) return;

                            // Fill inputs one by one
                            const inputs = document.querySelectorAll(
                              "input[name^='otp-']"
                            );
                            inputs.forEach((input, i) => {
                              input.value = paste[i] || '';
                            });

                            // Focus last filled input
                            const lastFilled =
                              Math.min(paste.length, inputs.length) - 1;
                            if (lastFilled >= 0) inputs[lastFilled].focus();
                          }}
                        />
                      ))}
                    </div>

                    <div className="flex justify-center mb-4">
                      {countdown > 0 ? (
                        <span className="px-4 py-1 text-sm font-bold ">
                          Resend OTP in {countdown}s
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleResendOtp(tempSignupData.email)}
                          className="px-4 py-1 text-sm font-semibold rounded-full bg-brand text-white shadow-md hover:shadow-lg transition"
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>

                    <motion.button
                      whileHover={{ scale: verifying ? 1 : 1.05 }}
                      whileTap={{ scale: verifying ? 1 : 0.95 }}
                      disabled={verifying}
                      onClick={() => {
                        const otp = Array.from({ length: 6 })
                          .map(
                            (_, idx) =>
                              document.querySelector(`input[name="otp-${idx}"]`)
                                ?.value
                          )
                          .join('');
                        if (otp.length !== 6) {
                          toast.error('Please enter all 6 digits of OTP');
                          return;
                        }
                        handleSignupVerify(otp);
                      }}
                      className={`w-full bg-gradient-to-r from-brandDark to-brand text-white py-2 rounded-lg shadow-md transition relative ${
                        verifying
                          ? 'opacity-70 cursor-not-allowed'
                          : 'hover:shadow-lg'
                      }`}
                    >
                      {verifying ? (
                        <div className="flex justify-center items-center">
                          <svg
                            className="animate-spin h-5 w-5 mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                            ></path>
                          </svg>
                          Verifying...
                        </div>
                      ) : (
                        'Verify & Continue'
                      )}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/login')}
                      className="w-full mt-3 bg-gray-200 text-gray-800 py-2 rounded-lg shadow-md hover:bg-gray-300 transition"
                    >
                      Go Back to Login
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right Side */}
          <div className="w-full md:w-1/2 flex items-center justify-center bg-[#DADDFB] overflow-hidden">
            <motion.img
              src={SignUpImage}
              alt="Login Illustration"
              className="max-w-[80%] max-h-[80%] object-contain"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: 'easeOut' }}
              loading="lazy"
            />
          </div>
        </div>
        {/* Footer */}
      </div>
      <FooterAuth />
    </>
  );
}
